import { useState } from "react";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import "./index.css";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import logImage from "../../assets/log.svg";
import { loginUsuario, loginPersonal, confirmOtp } from "../../services/AuthService";
import { esCorreoValido } from "../../utils/functions";
import { useUser } from "../../context/userContext";

const SignIn = ({ }) => {
  const navigate = useNavigate();
  const { fetchUserData } = useUser();

  const [credenciales, setCredenciales] = useState({
    cif: "",
    correoInstitucional: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleShowPassword = () => setShowPassword(!showPassword);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredenciales((prev) => ({ ...prev, [name]: value }));
  };

  const promptAndVerifyOtp = async (subject, hintText, afterSuccessNavigateTo) => {
    const mask = (str) => {
      if (!str) return "";

      if (str.includes("@")) {
        const [u, d] = str.split("@");
        const mu = u.length <= 2 ? u[0] + "*" : u[0] + "*".repeat(u.length - 2) + u.slice(-1);
        return `${mu}@${d}`;
      }

      return "*".repeat(Math.max(0, str.length - 2)) + str.slice(-2);
    };

    const subjectMasked = mask(subject);

    const { isConfirmed } = await Swal.fire({
      title: "Verificación en dos pasos",
      text: hintText || "Ingresa el código de 6 dígitos enviado a tu correo",
      input: "text",
      inputPlaceholder: "000000",
      inputAttributes: {
        maxlength: 6,
        autocapitalize: "off",
        inputmode: "numeric",
      },
      footer: `<span>Enviado a: <b>${subjectMasked}</b> · <a href="#" id="resend-otp">Reenviar código</a></span>`,
      showCancelButton: true,
      confirmButtonText: "Verificar",
      confirmButtonColor: "#0099a8",
      cancelButtonText: "Cancelar",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async (code) => {
        if (!code || code.trim().length !== 6) {
          Swal.showValidationMessage("Ingresa el código de 6 dígitos");
          return false;
        }
        try {
          await confirmOtp({ code: code.trim(), subject });
        } catch (e) {
          const msg =
            e?.response?.data ||
            "Código inválido o expirado. Intenta nuevamente.";
          Swal.showValidationMessage(msg);
          return false;
        }
        return true;
      },
      didOpen: () => {
        const link = document.getElementById("resend-otp");
        if (link) {
          link.addEventListener("click", async (ev) => {
            ev.preventDefault();
            try {
              if (esCorreoValido(subject)) {
                await loginPersonal({ correoInstitucional: subject, password: credenciales.password });
              } else {
                await loginUsuario({ cif: subject, password: credenciales.password });
              }
              Swal.showValidationMessage("Se envió un nuevo código a tu correo.");
            } catch {
              Swal.showValidationMessage("No se pudo reenviar el código. Intenta más tarde.");
            }
          });
        }
      },
    });

    if (isConfirmed) {
      await fetchUserData();
      navigate(afterSuccessNavigateTo, { replace: true });
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    const isStaff = esCorreoValido(credenciales.identificador);

    try {
      if (!isStaff) {
        const payload = {
          cif: credenciales.identificador,
          password: credenciales.password,
        };
        await loginUsuario(payload);

        await promptAndVerifyOtp(
          credenciales.identificador,
          "Hemos enviado un código de verificación a tu correo institucional.",
          "/menuestudiante"
        );
      } else {
        const payload = {
          correoInstitucional: credenciales.identificador,
          password: credenciales.password,
        };
        await loginPersonal(payload);

        await promptAndVerifyOtp(
          credenciales.identificador,
          "Hemos enviado un código de verificación a tu correo institucional.",
          "/menubiblioteca"
        );
      }
    } catch (e) {
      Swal.fire({
        title: e?.response?.data || "Error al iniciar sesión",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#21807C",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="sign-in-form" onSubmit={handleSignIn}>
      <h2 className="title">Inicio de Sesión</h2>
      <div className="input-field">
        <FaUser />
        <input
          name="identificador"
          type="text"
          placeholder="CIF"
          value={credenciales.identificador}
          onChange={handleChange}
        />
      </div>
      <div className="input-field">
        <FaLock />
        <input
          name="password"
          type={showPassword ? "text" : "password"}
          placeholder="Contraseña"
          value={credenciales.password}
          onChange={handleChange}
        />
        <span className="toggle-password" onClick={toggleShowPassword}>
          {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
        </span>
      </div>
      <button
        disabled={loading}
        style={{ cursor: loading ? "not-allowed" : "pointer" }}
        type="submit"
        className="btn solid"
      >
        {loading ? "Iniciando Sesión..." : "Iniciar Sesión"}
      </button>
    </form>
  );
};

const LoginTotal = () => {
  const { state } = useLocation();

  return (
    <div className="container">
      <div className="forms-container">
        <div className="signin-signup">
          <SignIn state={state} />
        </div>
      </div>
      <div className="panels-container">
        <div className="panel left-panel">
          <div className="content">
            <h3>UamLibrary: La biblioteca en la palma de tu mano</h3>
            <p>Inicia Sesión tal y como lo haces en UamVirtual!</p>
          </div>
          <img src={logImage} className="image" alt="Register" />
        </div>
      </div>
    </div>
  );
};

export default LoginTotal;
